import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtARS = (v) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 2 }).format(v ?? 0);

const fmtCant = (v) =>
    new Intl.NumberFormat('es-AR', { maximumFractionDigits: 2 }).format(v ?? 0);

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    return new Date(fechaStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const toDateStr = (d) => {
    if (!d) return null;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ── Estado inicial del formulario ─────────────────────────────────────────────

const initialForm = {
    materialId:    0,
    producto:      '',
    cantidad:      null,
    precioUnitario: null,
    fecha:         new Date(),
};

// ── Componente principal ──────────────────────────────────────────────────────

const Materiales = () => {
    const toast      = useRef(null);
    const HOST_API   = process.env.REACT_APP_URL;
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [materiales,   setMateriales]   = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [visible,      setVisible]      = useState(false);
    const [saving,       setSaving]       = useState(false);
    const [formData,     setFormData]     = useState(initialForm);
    const [stockDialog,  setStockDialog]  = useState({ visible: false, material: null, nuevaCantidad: null });
    const [globalFilter, setGlobalFilter] = useState('');
    const [soloSinStock, setSoloSinStock] = useState(false);

    const hc = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    // ── Fetch ──
    const fetchMateriales = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/materiales/GetMateriales?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setMateriales(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los materiales', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchMateriales(); }, [fetchMateriales]);

    // ── Abrir formulario ──
    const handleNuevo  = () => { setFormData(initialForm); setVisible(true); };
    const handleEditar = (row) => {
        setFormData({
            materialId:    row.materialId,
            producto:      row.producto,
            cantidad:      row.cantidad,
            precioUnitario: row.precioUnitario,
            fecha:         new Date(row.fecha),
        });
        setVisible(true);
    };

    // ── Guardar material ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.producto.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá el nombre del producto', life: 3000 });
            return;
        }
        if (formData.cantidad === null || formData.cantidad < 0) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingresá una cantidad válida', life: 3000 });
            return;
        }
        if (!formData.fecha) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná la fecha', life: 3000 });
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${HOST_API}api/materiales/SaveMaterial`, {
                materialId:    formData.materialId,
                administracionId,
                barrioId:      barrio.id,
                producto:      formData.producto.trim(),
                cantidad:      formData.cantidad ?? 0,
                precioUnitario: formData.precioUnitario ?? 0,
                fecha:         toDateStr(formData.fecha),
            });
            setVisible(false);
            fetchMateriales();
            toast.current.show({ severity: 'success', summary: 'Guardado', detail: 'Material guardado correctamente', life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo guardar el material', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    // ── Ajustar stock ──
    const handleOpenStock = (row) => setStockDialog({ visible: true, material: row, nuevaCantidad: row.cantidad });

    const handleConfirmStock = async () => {
        const { material, nuevaCantidad } = stockDialog;
        if (nuevaCantidad === null || nuevaCantidad < 0) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'La cantidad no puede ser negativa', life: 3000 });
            return;
        }
        try {
            await axios.put(`${HOST_API}api/materiales/UpdateStock?materialId=${material.materialId}&administracionId=${administracionId}&cantidad=${nuevaCantidad}`);
            setStockDialog({ visible: false, material: null, nuevaCantidad: null });
            fetchMateriales();
            toast.current.show({ severity: 'success', summary: 'Stock actualizado', detail: `${material.producto}: ${fmtCant(nuevaCantidad)} unidades`, life: 2500 });
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar el stock', life: 3000 });
        }
    };

    // ── Eliminar ──
    const handleEliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar "${row.producto}" del inventario?`,
            header: 'Eliminar material',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, eliminar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.delete(`${HOST_API}api/materiales/DeleteMaterial?materialId=${row.materialId}&administracionId=${administracionId}`);
                    fetchMateriales();
                    toast.current.show({ severity: 'info', summary: 'Eliminado', detail: 'Material eliminado del inventario', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el material', life: 3000 });
                }
            },
        });
    };

    // ── Filtros ──
    const materialesFiltrados = materiales.filter(m => {
        if (soloSinStock && m.cantidad > 0) return false;
        if (globalFilter) return m.producto.toLowerCase().includes(globalFilter.toLowerCase());
        return true;
    });

    // ── Totales para el footer ──
    const totalValor    = materialesFiltrados.reduce((acc, m) => acc + m.cantidad * m.precioUnitario, 0);
    const sinStockCount = materiales.filter(m => m.cantidad === 0).length;

    // ── Templates ──
    const cantidadTemplate = (row) => {
        const sin = row.cantidad === 0;
        const bajo = row.cantidad > 0 && row.cantidad <= 3;
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span style={{ fontWeight: sin ? 700 : 500, color: sin ? '#f04438' : bajo ? '#b54708' : '#101828' }}>
                    {fmtCant(row.cantidad)}
                </span>
                {sin && (
                    <span style={{ background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca', fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: 99 }}>
                        Sin stock
                    </span>
                )}
                {bajo && (
                    <span style={{ background: '#fffaeb', color: '#b54708', border: '1px solid #fedf89', fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: 99 }}>
                        Bajo
                    </span>
                )}
            </div>
        );
    };

    const precioTemplate  = (row) => fmtARS(row.precioUnitario);
    const totalTemplate   = (row) => <span style={{ fontWeight: 600 }}>{fmtARS(row.cantidad * row.precioUnitario)}</span>;
    const fechaTemplate   = (row) => fmtFecha(row.fecha);

    const accionesTemplate = (row) => (
        <div style={{ display: 'flex', gap: '0.15rem' }}>
            <Button icon="pi pi-box"    rounded text severity="info"    tooltip="Ajustar stock" tooltipOptions={{ position: 'left' }} onClick={() => handleOpenStock(row)} />
            <Button icon="pi pi-pencil" rounded text severity="warning" tooltip="Editar"        tooltipOptions={{ position: 'left' }} onClick={() => handleEditar(row)} />
            <Button icon="pi pi-trash"  rounded text severity="danger"  tooltip="Eliminar"      tooltipOptions={{ position: 'left' }} onClick={() => handleEliminar(row)} />
        </div>
    );

    // ── Render ──────────────────────────────────────────────────────────────
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <div>
                        <h4 style={{ margin: 0, color: '#101828' }}>Inventario de materiales</h4>
                        <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>Control de stock por barrio</p>
                    </div>
                    {sinStockCount > 0 && (
                        <span style={{ background: '#fef3f2', color: '#f04438', border: '1px solid #fecdca', fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: 99 }}>
                            {sinStockCount} sin stock
                        </span>
                    )}
                </div>
                <Button label="Nuevo material" icon="pi pi-plus" onClick={handleNuevo} disabled={!barrio?.id} />
            </div>

            {!barrio?.id
                ? <p style={{ color: '#98a2b3' }}>Seleccioná un barrio para ver el inventario.</p>
                : (
                    <>
                        {/* Filtros */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                <span className="p-input-icon-left">
                                    <i className="pi pi-search" style={{ left: '0.75rem' }} />
                                    <InputText
                                        value={globalFilter}
                                        onChange={(e) => setGlobalFilter(e.target.value)}
                                        placeholder="Buscar producto…"
                                        className="p-inputtext-sm"
                                        style={{ paddingLeft: '2rem', width: 220 }}
                                    />
                                </span>
                                <button
                                    onClick={() => setSoloSinStock(v => !v)}
                                    style={{
                                        background: soloSinStock ? '#fef3f2' : '#f9fafb',
                                        border: `1px solid ${soloSinStock ? '#fecdca' : '#d0d5dd'}`,
                                        color: soloSinStock ? '#f04438' : '#344054',
                                        borderRadius: 8, padding: '0.35rem 0.75rem',
                                        cursor: 'pointer', fontSize: '0.83rem', fontWeight: soloSinStock ? 700 : 500,
                                    }}
                                >
                                    <i className="pi pi-filter" style={{ marginRight: '0.4rem', fontSize: '0.78rem' }} />
                                    Solo sin stock
                                </button>
                            </div>
                            <span style={{ fontSize: '0.85rem', color: '#667085', fontWeight: 500 }}>
                                Valor total: <strong style={{ color: '#101828' }}>{fmtARS(totalValor)}</strong>
                            </span>
                        </div>

                        <DataTable
                            value={materialesFiltrados}
                            paginator
                            rows={20}
                            rowsPerPageOptions={[10, 20, 50]}
                            emptyMessage="No hay materiales en el inventario"
                            sortField="producto"
                            sortOrder={1}
                            rowClassName={(r) => r.cantidad === 0 ? 'opacity-60' : ''}
                            style={{ fontSize: '0.88rem' }}
                        >
                            <Column field="producto"       header="Producto"        sortable style={{ minWidth: 180 }} />
                            <Column field="cantidad"       header="Stock actual"    sortable body={cantidadTemplate} style={{ minWidth: 140 }} />
                            <Column field="precioUnitario" header="Precio unitario" sortable body={precioTemplate}   style={{ minWidth: 140 }} />
                            <Column header="Valor total"   body={totalTemplate}    style={{ minWidth: 140 }} />
                            <Column field="fecha"          header="Fecha ingreso"  sortable body={fechaTemplate}    style={{ minWidth: 120 }} />
                            <Column header=""              body={accionesTemplate} style={{ width: 115, textAlign: 'center' }} />
                        </DataTable>
                    </>
                )
            }

            {/* ── Dialog: Nuevo / Editar material ── */}
            <Dialog
                header={formData.materialId === 0 ? 'Nuevo material' : `Editar — ${formData.producto}`}
                visible={visible}
                style={{ width: 460 }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={lbl}>Producto <span style={req}>*</span></label>
                            <InputText
                                value={formData.producto}
                                onChange={(e) => hc('producto', e.target.value)}
                                className="w-full"
                                placeholder="Ej: Pintura látex blanca, Cable eléctrico 2.5mm…"
                                maxLength={200}
                                autoFocus
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                                <label style={lbl}>Cantidad <span style={req}>*</span></label>
                                <InputNumber
                                    value={formData.cantidad}
                                    onValueChange={(e) => hc('cantidad', e.value)}
                                    className="w-full"
                                    min={0}
                                    maxFractionDigits={2}
                                    placeholder="0"
                                    showButtons
                                    buttonLayout="horizontal"
                                    decrementButtonClassName="p-button-secondary"
                                    incrementButtonClassName="p-button-secondary"
                                    incrementButtonIcon="pi pi-plus"
                                    decrementButtonIcon="pi pi-minus"
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={lbl}>Precio unitario</label>
                                <InputNumber
                                    value={formData.precioUnitario}
                                    onValueChange={(e) => hc('precioUnitario', e.value)}
                                    className="w-full"
                                    mode="currency"
                                    currency="ARS"
                                    locale="es-AR"
                                    min={0}
                                    placeholder="$ 0,00"
                                />
                            </div>
                        </div>

                        {formData.cantidad !== null && formData.precioUnitario !== null && formData.cantidad > 0 && formData.precioUnitario > 0 && (
                            <div style={{ background: '#f0f9ff', border: '1px solid #b9e0ff', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.85rem', color: '#026aa2' }}>
                                Valor total: <strong>{fmtARS(formData.cantidad * formData.precioUnitario)}</strong>
                            </div>
                        )}

                        <div>
                            <label style={lbl}>Fecha de ingreso <span style={req}>*</span></label>
                            <Calendar
                                value={formData.fecha}
                                onChange={(e) => hc('fecha', e.value)}
                                dateFormat="dd/mm/yy"
                                maxDate={new Date()}
                                showIcon
                                className="p-inputtext-sm w-full"
                            />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button label={saving ? 'Guardando…' : 'Guardar'} icon={saving ? undefined : 'pi pi-check'} type="submit" disabled={saving} />
                        </div>
                    </div>
                </form>
            </Dialog>

            {/* ── Dialog: Ajustar stock ── */}
            <Dialog
                header={`Ajustar stock — ${stockDialog.material?.producto ?? ''}`}
                visible={stockDialog.visible}
                style={{ width: 360 }}
                modal
                onHide={() => setStockDialog({ visible: false, material: null, nuevaCantidad: null })}
            >
                {stockDialog.material && (
                    <div className="flex flex-column gap-3 mt-2">
                        <div style={{ background: '#f9fafb', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.65rem 0.85rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.85rem', color: '#667085' }}>Stock actual</span>
                            <span style={{
                                fontWeight: 700, fontSize: '1.1rem',
                                color: stockDialog.material.cantidad === 0 ? '#f04438' : '#101828'
                            }}>
                                {fmtCant(stockDialog.material.cantidad)}
                            </span>
                        </div>

                        <div>
                            <label style={lbl}>Nueva cantidad <span style={req}>*</span></label>
                            <InputNumber
                                value={stockDialog.nuevaCantidad}
                                onValueChange={(e) => setStockDialog(prev => ({ ...prev, nuevaCantidad: e.value }))}
                                className="w-full"
                                min={0}
                                maxFractionDigits={2}
                                showButtons
                                buttonLayout="horizontal"
                                decrementButtonClassName="p-button-secondary"
                                incrementButtonClassName="p-button-secondary"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                autoFocus
                            />
                        </div>

                        {stockDialog.nuevaCantidad !== null && stockDialog.nuevaCantidad !== stockDialog.material.cantidad && (
                            <div style={{
                                background: stockDialog.nuevaCantidad === 0 ? '#fef3f2' : '#ecfdf3',
                                border: `1px solid ${stockDialog.nuevaCantidad === 0 ? '#fecdca' : '#a9efc5'}`,
                                borderRadius: 8, padding: '0.5rem 0.75rem',
                                fontSize: '0.83rem',
                                color: stockDialog.nuevaCantidad === 0 ? '#b42318' : '#027a48',
                            }}>
                                {stockDialog.nuevaCantidad === 0
                                    ? '⚠ El producto quedará sin stock'
                                    : `Diferencia: ${fmtCant(stockDialog.nuevaCantidad - stockDialog.material.cantidad)} unidades`
                                }
                            </div>
                        )}

                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" severity="secondary" text onClick={() => setStockDialog({ visible: false, material: null, nuevaCantidad: null })} />
                            <Button label="Confirmar" icon="pi pi-check" onClick={handleConfirmStock} />
                        </div>
                    </div>
                )}
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req = { color: '#f04438' };

export default Materiales;
